import React, { useEffect, useRef, useState } from "react";
import { X, Send, Brain, Loader } from "lucide-react";

const card = "rounded-2xl border border-[#00ff90]/20 bg-[#080808] shadow-[0_0_24px_#00ff9010]";
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

export default function KiraInsightPanel({ context, initialPrompt, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);

  // Build KIRA system context from dashboard data
  const buildSystemPrompt = (ctx) => {
    if (!ctx) return "You are KIRA, an intelligent HR and workforce analytics advisor for KweryAI.";
    const w = ctx.workforce    || {};
    const p = ctx.payroll      || {};
    const a = ctx.attendance   || {};
    const perf = ctx.performance || {};
    const t = ctx.tickets      || {};

    return `You are KIRA, an intelligent HR and workforce analytics advisor for KweryAI. You have access to real-time workforce data.

CURRENT WORKFORCE METRICS:
- Headcount: ${w.headcount ?? "N/A"} employees (growth: ${w.headcount_growth_pct ?? "N/A"}%)
- Attrition rate: ${w.attrition_rate ?? "N/A"}% (rolling 12 months)
- Monthly payroll cost: £${p.monthly_cost ? (p.monthly_cost/1000).toFixed(0)+"k" : "N/A"}
- Payroll exceptions: ${p.exceptions_open ?? 0} unresolved
- Attendance rate today: ${a.today_rate ?? "N/A"}%
- Monthly attendance avg: ${a.monthly_avg ?? "N/A"}%
- Performance review completion: ${perf.review_completion ?? "N/A"}%
- Average performance rating: ${perf.avg_rating ?? "N/A"}
- High performers: ${perf.high_performers ?? "N/A"}, At-risk: ${perf.at_risk_count ?? 0}
- Open P1 tickets: ${t.open_p1 ?? 0}
- SLA breach rate: ${t.sla_breach_rate ?? "N/A"}%

Provide concise, strategic, actionable advice. Be direct. Use bullet points for action items. When recommending actions, be specific about who should do what and by when. Highlight risks clearly. Reference the actual numbers from the data above in your responses.`;
  };

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: "user", content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const systemPrompt = buildSystemPrompt(context);
      const fullMessage  = `${systemPrompt}\n\nQ: ${text}`;

      const token = localStorage.getItem("token");
      let userId = "anon";
      try {
        userId = JSON.parse(localStorage.getItem("user") || "null")?.id || "anon";
      } catch {}

      const res = await fetch(`${API_BASE}/api/ai/chat`, {
        method: "POST",
        headers: {
          Authorization:  `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message:    fullMessage,
          surface:    "executive_insight",
          session_id: `exec_insight_${userId}`,
        }),
      });
      const data  = await res.json();
      const reply = data.reply || data.message || "I couldn't process that. Please try again.";
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "⚠ Connection error. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  // Send initial prompt on open
  useEffect(() => {
    if (initialPrompt) sendMessage(initialPrompt);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className={`${card} w-full max-w-2xl flex flex-col`} style={{ maxHeight: "85vh" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-[#00ff90]/15 border border-[#00ff90]/30 flex items-center justify-center">
              <Brain size={15} className="text-[#00ff90]" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">KIRA</p>
              <p className="text-xs text-[#00ff90]/50">Workforce Intelligence Advisor</p>
            </div>
            <div className="flex items-center gap-1.5 ml-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#00ff90] animate-pulse" />
              <span className="text-xs text-[#00ff90]/40">Live data connected</span>
            </div>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 min-h-0">
          {messages.length === 0 && !loading && (
            <div className="text-center py-8">
              <p className="text-white/20 text-sm">Ask KIRA anything about your workforce data...</p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {[
                  "What are my top 3 risks right now?",
                  "Which department has the highest attrition?",
                  "Forecast payroll for next quarter",
                  "Who needs a performance review urgently?",
                ].map(q => (
                  <button key={q} onClick={() => sendMessage(q)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:border-[#00ff90]/30 hover:text-[#00ff90]/60 transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[#00ff90]/10 border border-[#00ff90]/20 text-white/80"
                  : "bg-white/5 border border-white/5 text-white/70"
              }`}>
                {m.role === "assistant" && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <Brain size={11} className="text-[#00ff90]/50" />
                    <span className="text-xs text-[#00ff90]/40 font-medium">KIRA</span>
                  </div>
                )}
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-white/5 border border-white/5 rounded-xl px-4 py-3 flex items-center gap-2">
                <Loader size={13} className="text-[#00ff90]/50 animate-spin" />
                <span className="text-xs text-white/30">KIRA is analysing...</span>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="shrink-0 px-5 pb-5 pt-3 border-t border-white/5">
          <div className="flex items-center gap-3 bg-black/60 border border-[#00ff90]/20 rounded-xl px-4 py-2 focus-within:border-[#00ff90]/40 transition-colors">
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage(input)}
              placeholder="Ask KIRA about your workforce data..."
              className="flex-1 bg-transparent text-sm text-white outline-none placeholder-white/20"
            />
            <button onClick={() => sendMessage(input)} disabled={!input.trim() || loading}
              className="w-8 h-8 rounded-lg bg-[#00ff90]/15 border border-[#00ff90]/30 flex items-center justify-center text-[#00ff90] hover:bg-[#00ff90]/25 disabled:opacity-30 transition-colors shrink-0">
              <Send size={13} />
            </button>
          </div>
          <p className="text-xs text-white/15 mt-2 text-center">
            KIRA has access to your live workforce metrics • Responses are AI-generated suggestions
          </p>
        </div>
      </div>
    </div>
  );
}